import {useEffect, useState} from 'react';
import {Box, CircularProgress, Dialog, Grid, InputAdornment, TextField, Typography} from '@mui/material';
import {Search as SearchIcon} from '@mui/icons-material';

const GIPHY_API_URL = 'https://api.giphy.com/v1/gifs';

export default function GifPickerDialog({
                                          open = false,
                                          onClose,
                                          onGifSelect
                                        }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [gifs, setGifs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchGifs = async (term) => {
    setIsLoading(true);
    setError(null);

    try {
      const endpoint = term
          ? `${GIPHY_API_URL}/search`
          : `${GIPHY_API_URL}/trending`;

      const params = new URLSearchParams({
        api_key: process.env.REACT_APP_GIPHY_API_KEY,
        limit: 50,
        rating: 'g',
        ...(term && { q: term })
      });

      const response = await fetch(`${endpoint}?${params}`);

      if (!response.ok) {
        setError('Fehler beim Laden der GIFs. Server antwortet nicht.');
        console.error('HTTP Error:', response.status, response.statusText);
        return;
      }

      const data = await response.json();

      if (!data || !Array.isArray(data.data)) {
        setError('Ungültiges Datenformat von der API erhalten.');
        console.error('Invalid API Response:', data);
        return;
      }

      setGifs(data.data);
    } catch (err) {
      let errorMessage = 'Fehler beim Laden der GIFs. Bitte versuche es später erneut.';

      if (err instanceof TypeError && err.message.includes('fetch')) {
        errorMessage = 'Keine Internetverbindung. Bitte überprüfe deine Verbindung.';
      }

      setError(errorMessage);
      console.error('Giphy API Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      searchGifs('');
    }
  }, [open]);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    const trimmedTerm = term.trim();

    // Delay search for empty string to avoid unnecessary API calls
    if (trimmedTerm.length === 0) {
      setTimeout(() => searchGifs(''), 300);
    } else {
      // Add minimal debounce for search terms
      const timeoutId = setTimeout(() => searchGifs(trimmedTerm), 300);
      return () => clearTimeout(timeoutId);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
      <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            className: 'dialog-glass',
            sx: {
              height: '80vh'
            }
          }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          <Box sx={{ p: 2, pb: 0 }}>
            <Typography variant="h6" className="gradient-text">
              GIF auswählen
            </Typography>
          </Box>

          <Box sx={{ p: 2, pb: 1 }}>
            <TextField
                fullWidth
                size="small"
                placeholder="GIF suchen..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
                className="giphy-search-input"
                InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                  ),
                }}
            />
          </Box>

          <Box sx={{
            flex: 1,
            overflow: 'hidden'
          }}>
            {isLoading ? (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}>
                  <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ p: 2 }}>
                  <Typography color="error" align="center">
                    {error}
                  </Typography>
                </Box>
            ) : (
                <Box sx={{
                  height: '100%',
                  overflowY: 'auto',
                  p: 2,
                  pt: 1
                }}>
                  <Grid container spacing={1}>
                    {gifs.map((gif) => (
                        <Grid item xs={6} sm={4} md={3} key={gif.id}>
                          <img
                              src={gif.images.fixed_height.url}
                              alt={gif.title}
                              className="giphy-gif-img"
                              onClick={() => onGifSelect(gif.images.original.url)}
                          />
                        </Grid>
                    ))}
                  </Grid>
                </Box>
            )}
          </Box>
        </Box>
      </Dialog>
  );
}